/**
 * Archived boards (FOR-349, PUT /api/boards/{key} with `archived`).
 *
 * An archived board leaves GET /api/boards (so the sidebar and every picker
 * drop it), its tasks stay readable but refuse writes (403 `board_archived`),
 * and its webhooks and app tokens pause. Managers and owners archive and
 * unarchive; General never. The pieces here are shared by Tasks → Settings,
 * the board page and the task detail.
 */

import { Archive, ArchiveRestore } from "lucide-react"
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Badge, SButton } from "@/components/studio/ui"
import { rs } from "@/components/studio/ui-classes"
import { useSetBoardArchived } from "@/hooks/use-board-archive"
import { ARCHIVE_BOARD_EXPLAINER, formatArchivedDate } from "@/lib/board-archive"
import { BOARD_ARCHIVED_MESSAGE, type Board, type BoardActivityEntry } from "@/lib/task-boards"
import { cn } from "@/lib/utils"

export function ArchivedBoardBadge({ label = "Archived board", className }: { label?: string; className?: string }) {
  return (
    <Badge tone="warning" className={className}>
      <Archive className="h-3 w-3" aria-hidden />
      {label}
    </Badge>
  )
}

/** The confirm step before archiving. */
export function ArchiveBoardDialog({
  board,
  open,
  onOpenChange,
  onArchived,
}: {
  board: Pick<Board, "key" | "name">
  open: boolean
  onOpenChange: (open: boolean) => void
  onArchived?: () => void
}) {
  const archive = useSetBoardArchived()
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Archive {board.name}?</AlertDialogTitle>
          <AlertDialogDescription>{ARCHIVE_BOARD_EXPLAINER}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          {/* A plain button, not AlertDialogAction: the dialog stays up until the API answers. */}
          <SButton
            kind="primary"
            disabled={archive.isPending}
            onClick={() =>
              archive.mutate(
                { board, archived: true },
                {
                  onSuccess: () => {
                    onOpenChange(false)
                    onArchived?.()
                  },
                },
              )
            }
          >
            <Archive className="h-4 w-4" />
            {archive.isPending ? "Archiving…" : "Archive board"}
          </SButton>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

export function UnarchiveButton({
  board,
  kind = "secondary",
  onDone,
}: {
  board: Pick<Board, "key" | "name">
  kind?: "primary" | "secondary" | "small"
  onDone?: () => void
}) {
  const unarchive = useSetBoardArchived()
  return (
    <SButton
      kind={kind}
      disabled={unarchive.isPending}
      onClick={() => unarchive.mutate({ board, archived: false }, { onSuccess: () => onDone?.() })}
    >
      <ArchiveRestore className="h-4 w-4" />
      {unarchive.isPending ? "Unarchiving…" : "Unarchive"}
    </SButton>
  )
}

/** Across the top of an archived board's task list, reached by its URL. */
export function ArchivedBoardBanner({ board }: { board: Board }) {
  const when = formatArchivedDate(board.archived_at)
  return (
    <div className={cn(rs.card, "flex flex-wrap items-center gap-3 px-4 py-3")}>
      <ArchivedBoardBadge label="Archived" />
      <p className="min-w-0 flex-1 text-sm text-muted-foreground">
        {when ? `${board.name} was archived on ${when}. ` : `${board.name} is archived. `}
        Its tasks are read-only.
      </p>
      {board.can_manage && <UnarchiveButton board={board} />}
    </div>
  )
}

/** Above the fields of a task on an archived board. */
export function BoardReadOnlyNote({ className }: { className?: string }) {
  return (
    <div role="note" className={cn("flex flex-wrap items-center gap-2 rounded-2xl border border-[#eee] px-3 py-2 dark:border-white/[0.06]", className)}>
      <ArchivedBoardBadge />
      <span className="text-sm text-muted-foreground">{BOARD_ARCHIVED_MESSAGE}</span>
    </div>
  )
}

/** Who archived or unarchived the board, and when (GET /boards/{key} `activity`). */
export function BoardActivityList({ activity }: { activity: BoardActivityEntry[] }) {
  if (activity.length === 0) return null
  return (
    <div className="space-y-2">
      <span className={rs.label}>Activity</span>
      <ul className={cn("rounded-2xl border", rs.rule)}>
        {activity.map((entry, index) => (
          <li key={`${entry.created_at}-${index}`} className={cn("flex items-center gap-2 border-t px-3 py-2 text-sm first:border-t-0", rs.rule)}>
            {entry.action === "archived" ? (
              <Archive className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
            ) : (
              <ArchiveRestore className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
            )}
            <span className="min-w-0 flex-1 truncate">
              {entry.action === "archived" ? "Archived" : "Unarchived"} by {entry.user?.name ?? "someone no longer in Forge"}
            </span>
            <span className="shrink-0 text-xs text-muted-foreground">{formatArchivedDate(entry.created_at)}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}
